import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import '@ant-design/compatible/assets/index.css';
import { Input } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import Mapbox from '../mapbox/Mapbox';

require('./LocationAutocomplete.less');

const parseAddressComponents = (addressComponents) => {
	return addressComponents.reduce((acc, ac) => {
		ac.types.forEach((t) => (acc[t] = ac.long_name));
		return acc;
	}, {});
};

const extractCity = (parsedComponents) => {
	return nullSafeGetOrElse(
		'locality',
		parsedComponents,
		nullSafeGetOrElse(
			'sublocality',
			parsedComponents,
			nullSafeGetOrElse(
				'neighborhood',
				parsedComponents,
				nullSafeGetOrElse(
					'administrative_area_level_3',
					parsedComponents,
					nullSafeGet('administrative_area_level_2', parsedComponents)
				)
			)
		)
	);
};

const buildStreetAddress = (parsedComponents) => {
	const streetNumber = parsedComponents.street_number;
	const route = parsedComponents.route;
	return [streetNumber, route].filter(Boolean).join(' ');
};

export const LocationAutocomplete = (props) => {
	const { placeholder, lat, lon, disabled, onSelect, size } = props;

	const initialStreetAddress1 = nullSafeGetOrElse('value.streetAddress1', props, '');
	const initialStreetAddress2Opt = nullSafeGetOrElse('value.streetAddress2Opt', props, '');

	const [streetAddress2Opt, setStreetAddress2Opt] = useState(initialStreetAddress2Opt);
	const [parsedAddressState, setParsedAddressState] = useState({
		city: nullSafeGetOrElse('value.city', props, ''),
		region: nullSafeGetOrElse('value.region', props, ''),
		postcode: nullSafeGetOrElse('value.postcode', props, ''),
		country: nullSafeGetOrElse('value.country', props, ''),
	});

	const {
		ready,
		value,
		setValue,
		suggestions: { status, data },
		clearSuggestions,
	} = usePlacesAutocomplete({
		requestOptions: {
			types: ['address'],
		},
		debounce: 300,
	});

	useEffect(() => {
		if (initialStreetAddress1 && !value) {
			setValue(initialStreetAddress1, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const prevPropsRef = useRef({
		streetAddress1: initialStreetAddress1,
		streetAddress2Opt: initialStreetAddress2Opt,
	});

	const streetAddress1FromProps = nullSafeGetOrElse('value.streetAddress1', props, '');
	const streetAddress2OptFromProps = nullSafeGetOrElse('value.streetAddress2Opt', props, '');
	const cityFromProps = nullSafeGetOrElse('value.city', props, '');
	const regionFromProps = nullSafeGetOrElse('value.region', props, '');
	const postcodeFromProps = nullSafeGetOrElse('value.postcode', props, '');
	const countryFromProps = nullSafeGetOrElse('value.country', props, '');

	useEffect(() => {
		if (
			streetAddress1FromProps &&
			streetAddress1FromProps !== prevPropsRef.current.streetAddress1
		) {
			setValue(streetAddress1FromProps, false);
			clearSuggestions();
			prevPropsRef.current.streetAddress1 = streetAddress1FromProps;
		}

		if (streetAddress2OptFromProps !== prevPropsRef.current.streetAddress2Opt) {
			setStreetAddress2Opt(streetAddress2OptFromProps);
			prevPropsRef.current.streetAddress2Opt = streetAddress2OptFromProps;
		}

		setParsedAddressState({
			city: cityFromProps,
			region: regionFromProps,
			postcode: postcodeFromProps,
			country: countryFromProps,
		});
	}, [
		streetAddress1FromProps,
		streetAddress2OptFromProps,
		cityFromProps,
		regionFromProps,
		postcodeFromProps,
		countryFromProps,
		setValue,
		clearSuggestions,
	]);

	const processAddress = useCallback(
		(addressText) => {
			if (!addressText) return;

			getGeocode({ address: addressText })
				.then((results) => {
					if (results.length > 0) {
						const result = results[0];
						const parsedComponents = parseAddressComponents(result.address_components);
						const latLng = getLatLng(result);

						const parsedAddress = {
							streetAddress1: buildStreetAddress(parsedComponents),
							streetAddress2Opt: streetAddress2Opt,
							city: extractCity(parsedComponents),
							region: parsedComponents.administrative_area_level_1,
							postcode: parsedComponents.postal_code,
							country: parsedComponents.country,
						};

						setParsedAddressState({
							city: parsedAddress.city,
							region: parsedAddress.region,
							postcode: parsedAddress.postcode,
							country: parsedAddress.country,
						});

						onSelect(addressText, latLng, parsedAddress);
					}
				})
				.catch((error) => {
					console.log('Geocode error:', error);
				});
		},
		[streetAddress2Opt, onSelect]
	);

	const handleSelect = useCallback(
		(selectedAddress) => {
			setValue(selectedAddress, false);
			clearSuggestions();
			processAddress(selectedAddress);
		},
		[setValue, clearSuggestions, processAddress]
	);

	const handleBlur = useCallback(() => {
		if (value) {
			processAddress(value);
		}
	}, [value, processAddress]);

	const handleChange = useCallback(
		(text) => {
			setValue(text, true);
		},
		[setValue]
	);

	const handleStreetAddress2OptBlur = useCallback(() => {
		processAddress(value);
	}, [processAddress, value]);

	const cityRegionPostcode =
		`${parsedAddressState.city} ${parsedAddressState.region} ${parsedAddressState.postcode}`.trim();

	return (
		<div>
			<div className="flex flex-row gap-x-4">
				<div
					className="locationAutocomplete__container"
					style={{ minWidth: '50%', position: 'relative' }}
				>
					<Input
						size={size}
						value={value}
						onChange={(e) => handleChange(e.target.value)}
						onBlur={handleBlur}
						placeholder={placeholder}
						className="location-search-input"
						autoComplete="random"
						disabled={disabled || !ready}
					/>

					{status === 'OK' && data.length > 0 ? (
						<div className="locationAutocomplete__dropdownContainer">
							{data.map((suggestion) => (
								<div
									key={suggestion.place_id}
									onClick={() => handleSelect(suggestion.description)}
									className="locationAutocomplete__suggestionItem"
								>
									<span>{suggestion.description}</span>
								</div>
							))}
						</div>
					) : null}
				</div>
				<Input
					disabled={disabled}
					placeholder="PO#, Apt, suite, unit, building, floor, etc."
					value={streetAddress2Opt}
					onChange={(e) => setStreetAddress2Opt(e.target.value)}
					onBlur={handleStreetAddress2OptBlur}
				/>
			</div>

			<div className="flex flex-row gap-x-4">
				<Input disabled placeholder="City" value={cityRegionPostcode} style={{ marginTop: 16 }} />
			</div>

			{lon && lat ? (
				<div style={{ height: 240, width: 240, marginTop: 16, position: 'relative' }}>
					<Mapbox longitude={lon} latitude={lat} baseLayerStyle="light" zoomLevel={7} />
				</div>
			) : null}
		</div>
	);
};
